import type { AxiosError } from "axios";
import api from "./api";

export type PaymentProfileAddress = {
  street: string;
  number: string;
  complement?: string;
  district: string;
  city: string;
  state: string;
  postal_code: string;
};

export type PaymentProfileCard = {
  number: string;
  holder_name: string;
  expiry_month: string;
  expiry_year: string;
  cvv: string;
};

export type PaymentProfilePayload = {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  birth_date?: string;
  address: PaymentProfileAddress;
  card: PaymentProfileCard;
};

export type PagBankCard = {
  id: number;
  last_digits: string;
  brand: string | null;
  exp_month: string;
  exp_year: string;
  holder_name: string;
  is_default: boolean;
};

export type PaymentProfileOkResponse = {
  success: true;
  perfil_id?: number;
  card?: {
    last_digits: string;
    brand?: string | null;
    exp_month?: string;
    exp_year?: string;
    holder_name?: string;
  };
};

export type PagBankErrorBody = {
  error?: string;
  code?: string;
  message?: string;
  msg?: string;
};

export function getPagbankErrorMessage(err: unknown): string {
  const ax = err as AxiosError<PagBankErrorBody>;
  const d = ax.response?.data;
  const base =
    d?.error ||
    d?.message ||
    d?.msg ||
    "Não foi possível concluir a operação. Tente novamente.";
  if (d?.code === "already_registered") {
    return `${base} Use a área de pagamento para alterar o cartão.`;
  }
  return base;
}

/** Corpo de erro + código HTTP implícito (via Axios). */
export function getPagBankErrorFromUnknown(
  err: unknown,
  fallback = "Cartão recusado. Você pode tentar novamente ou usar outro cartão.",
): { error: string; code?: string } {
  const ax = err as AxiosError<PagBankErrorBody>;
  const d = ax.response?.data;
  const error =
    d?.error || d?.message || d?.msg || fallback;
  const code = typeof d?.code === "string" ? d.code : undefined;
  return { error, code };
}

export async function createPaymentProfile(
  payload: PaymentProfilePayload,
): Promise<PaymentProfileOkResponse> {
  const { data } = await api.post<PaymentProfileOkResponse>(
    "/pagbank/payment-profile/",
    payload,
  );
  return data;
}

export async function replaceCard(
  payload: PaymentProfilePayload,
): Promise<PaymentProfileOkResponse> {
  const { data } = await api.post<PaymentProfileOkResponse>(
    "/pagbank/replace-card/",
    payload,
  );
  return data;
}

export type GetCardsResponse = {
  cards: PagBankCard[];
  tax_id?: string;
  address_hint?: PaymentProfileAddress;
};

export async function getCards(): Promise<GetCardsResponse> {
  const { data } = await api.get<GetCardsResponse>("/pagbank/cards/");
  return data;
}
